import React from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';

import { MediaHint, MediaRef, MeFragment, EditUserInput } from '$/GraphQL/schema/schema';
import { WithUploadImage } from '$/components/WithUploadImage/WithUploadImage';
import { Avatar, Spinner } from '$/images/Icons';
import { AddButton, FloatingRemoveButton } from '../Button/Button';
import Text from '../Text';
import { generateId } from '$/utils/number';
import { useDeepCompareMemo } from '$/hooks/useHasChanged';
import ImgixSafe from './ImgixSafe';
import { getImgixCropParams } from '$/utils/imgix';

export interface AvatarPictureUploadProps {
  className?: string;
  value?: MediaRef;
  onChange?: (url: MediaRef | null | undefined) => void;
  size?: { width: number; height: number };
  addOnLeft?: boolean;
  text?: string | React.ReactNode;
  asset: {
    entity: string;
    entityId?: string;
    name: string;
    container: {
      width: number;
      height: number;
      hint: MediaHint;
    };
    fit: 'cover' | 'contain';
    position: 'top center' | 'center center' | 'bottom center';
  };
}

const AvatarPictureUpload: React.FC<AvatarPictureUploadProps> = ({
  size,
  className,
  addOnLeft,
  asset: assetProp,
  text,
  ...props
}) => {
  const dim = React.useMemo(() => size ?? { width: 75, height: 75 }, [size]);
  const refValue = props.value;
  const imageUrl = refValue?.imageUrl;

  const uuid = React.useMemo(() => generateId(), []);
  const asset = useDeepCompareMemo({ ...assetProp, entityId: assetProp.entityId ?? uuid });

  return (
    <div
      className={`avatar-picture-wrap ${className}`}
      css={css`
        ${tw`flex justify-between md:w-full`}
        margin-bottom: 1rem;
      `}
    >
      <WithUploadImage
        avatar
        name={asset.name}
        value={refValue}
        onChange={props.onChange}
        asset={asset}
      >
        {({ loading, onRemove }): JSX.Element => (
          <React.Fragment>
            <div className='relative'>
              {imageUrl ? (
                <FloatingRemoveButton floatLeft={addOnLeft} onClick={onRemove} />
              ) : (
                <AddButton />
              )}
              <div
                className='image-show cursor-pointer hover:bg-gray-300 bg-gray-200 bg-cover border rounded-full overflow-hidden flex items-center justify-center'
                css={css`
                  width: ${dim.width}px;
                  height: ${dim.height}px;
                `}
              >
                {!imageUrl && !loading && <Avatar className='h-2e' />}

                {imageUrl && (
                  <ImgixSafe
                    refValue={refValue}
                    imgixParams={{
                      w: dim.width,
                      h: dim.height,
                      mask: 'ellipse',
                      fit: 'facearea',
                      ar: 1,
                      faceindex: 1,
                      facepad: 5,
                      ...getImgixCropParams(refValue, {}),
                    }}
                  />
                )}
                {loading && <Spinner className='text-color-iconGrey h-2/3 absolute' />}
              </div>
            </div>
            {text && (
              <div className='flex ml-5 cursor-pointer items-center'>
                {typeof text === 'string' ? (
                  <Text.subhead className='mb-1/8e text-gray-0 leading-normal'>{text}</Text.subhead>
                ) : (
                  text
                )}
              </div>
            )}
          </React.Fragment>
        )}
      </WithUploadImage>
    </div>
  );
};

export { AvatarPictureUpload };

export interface ProfilePictureUploadProps {
  className?: string;
  value?: MediaRef;
  user: Partial<EditUserInput> | MeFragment;
  onChange?: (url: MediaRef | null | undefined) => void;
  size?: { width: number; height: number };
  addOnLeft?: boolean;
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  user,
  size,
  className,
  addOnLeft,
  ...props
}) => {
  const dim = React.useMemo(() => size ?? { width: 75, height: 75 }, [size]);

  return (
    <div className={className}>
      <AvatarPictureUpload
        value={props.value}
        onChange={props.onChange}
        size={size}
        addOnLeft={addOnLeft}
        text={
          <div className='flex flex-col justify-start'>
            <Text.subhead className='mb-1/8e text-gray-0 leading-normal cursor-text'>
              {`${user.firstName} ${user.lastName}`}
            </Text.subhead>
            <Text.body className='text-teal-4 text-md leading-tight font-medium'>
              Upload new photo
            </Text.body>
          </div>
        }
        asset={{
          entity: 'user',
          entityId: user.id,
          name: 'profile',
          container: {
            ...dim,
            hint: MediaHint.Profile,
          },
          fit: 'cover',
          position: 'center center',
        }}
      />
    </div>
  );
};

export default ProfilePictureUpload;
